import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const STORAGE_KEY = "catawiki-watchlist-v1";
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// localStorage shape: { items: { [lotId: number]: Lot }, savedAt: { [lotId]: ISO } }
function readStore() {
  if (typeof window === "undefined") return { items: {}, savedAt: {} };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: {}, savedAt: {} };
    const parsed = JSON.parse(raw);
    return {
      items: parsed.items || {},
      savedAt: parsed.savedAt || {},
    };
  } catch {
    return { items: {}, savedAt: {} };
  }
}

function writeStore(store) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // quota exceeded, ignore
  }
}

// Cross-tab + cross-component sync via a custom event.
const EVENT_NAME = "catawiki-watchlist-changed";

function broadcast(store) {
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: store }));
}

export default function useWatchlist() {
  const [store, setStore] = useState(readStore);

  useEffect(() => {
    const handler = (e) => setStore(e.detail || readStore());
    const storageHandler = (e) => {
      if (e.key === STORAGE_KEY) setStore(readStore());
    };
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", storageHandler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, []);

  const ids = Object.keys(store.items).map(Number);
  const items = ids
    .map((id) => store.items[id])
    .filter(Boolean)
    .sort((a, b) => {
      // Sort by closing time ascending (sooner first); closed/unknown last.
      const sa = a.seconds_remaining ?? Number.POSITIVE_INFINITY;
      const sb = b.seconds_remaining ?? Number.POSITIVE_INFINITY;
      return sa - sb;
    });

  const isSaved = useCallback((lotId) => !!store.items[lotId], [store.items]);

  const add = useCallback((lot) => {
    const next = readStore();
    next.items[lot.id] = lot;
    next.savedAt[lot.id] = new Date().toISOString();
    writeStore(next);
    setStore(next);
    broadcast(next);
  }, []);

  const remove = useCallback((lotId) => {
    const next = readStore();
    delete next.items[lotId];
    delete next.savedAt[lotId];
    writeStore(next);
    setStore(next);
    broadcast(next);
  }, []);

  const toggle = useCallback((lot) => {
    const current = readStore();
    if (current.items[lot.id]) {
      delete current.items[lot.id];
      delete current.savedAt[lot.id];
    } else {
      current.items[lot.id] = lot;
      current.savedAt[lot.id] = new Date().toISOString();
    }
    writeStore(current);
    setStore(current);
    broadcast(current);
  }, []);

  // Re-fetches fresh bid/time/reserve info for every saved lot and merges it
  // back into localStorage. Resolves with a summary.
  const refresh = useCallback(async () => {
    const current = readStore();
    const idList = Object.keys(current.items);
    if (idList.length === 0) return { refreshed: 0 };
    const { data } = await axios.get(`${API_URL}/lots/refresh`, {
      params: { ids: idList.join(",") },
    });
    const updates = data?.updates || {};
    let refreshed = 0;
    for (const idStr of idList) {
      const upd = updates[idStr];
      const lot = current.items[idStr];
      if (!lot || !upd || upd.missing) continue;
      current.items[idStr] = { ...lot, ...upd };
      refreshed++;
    }
    writeStore(current);
    setStore(current);
    broadcast(current);
    return { refreshed };
  }, []);

  return {
    items,
    count: ids.length,
    isSaved,
    add,
    remove,
    toggle,
    refresh,
  };
}
